import {
  CreateScreeningOutcome,
  ChangeScreeningOutcome,
  RemoveScreeningOutcomeConflictResolution,
  UndoScreeningOutcomeManualOverride,
} from '@/graphql';
import { apolloClient } from '@/plugins/apollo';
import { v4 as createUUID } from 'uuid';
import { ScreeningOutcome } from '@/models';
import { ScreeningOutcomeResolution, AssessmentState } from '@/enums';
import {
  refreshScreeningCounts,
  refreshScreeningInsights,
} from '@/modules/screeningCountsRefresh';

const actions = {
  resolveConflict(context, { reference, outcome, resolvedBy }) {
    return context.dispatch(
      'save',
      new ScreeningOutcome({
        reference,
        outcome,
        resolvedBy,
        resolution: ScreeningOutcomeResolution.CONFLICT_RESOLUTION,
      })
    );
  },
  resolveConflictOptimistically(
    context,
    { reference, outcome, resolvedBy }
  ) {
    return context.dispatch(
      'saveOptimistically',
      new ScreeningOutcome({
        reference,
        outcome,
        resolvedBy,
        resolution: ScreeningOutcomeResolution.CONFLICT_RESOLUTION,
      })
    );
  },
  async save(context, screeningOutcome) {
    return context.dispatch('internalSave', {
      screeningOutcome,
    });
  },
  async saveOptimistically(context, screeningOutcome) {
    return context.dispatch('internalSave', {
      screeningOutcome,
      optimistic: true,
    });
  },
  async internalSave(
    context,
    { screeningOutcome, optimistic = false }
  ) {
    const variables = {
      screeningOutcome: screeningOutcome.creationData(),
    };
    const mutation = screeningOutcome.id
      ? ChangeScreeningOutcome
      : CreateScreeningOutcome;
    const key = screeningOutcome.id
      ? 'changeScreeningOutcome'
      : 'createScreeningOutcome';

    let optimisticResponse;
    let refetchQueries;
    if (optimistic) {
      const optimisticId = createUUID();
      optimisticResponse = {
        [key]: {
          ...screeningOutcome,
          reference: {
            ...screeningOutcome.reference,
            screeningState: AssessmentState.COMPLETE,
          },
          id: optimisticId,
          optimisticId,
        },
      };
      optimisticResponse[key].reference.screeningOutcome = {
        ...optimisticResponse[key],
        reference: null,
      };
    } else {
      refetchQueries = [
        'GetReference',
        'GetReferencesByReviewIdAndFilters',
        'GetReferencesByFilters',
        'SearchReferences',
        'GetRelevanceAssessmentCountsByReviewIdAndFilters',
        'GetScreeningOutcomesByReviewId',
      ];
    }

    const response = await apolloClient.mutate({
      mutation,
      variables,
      optimisticResponse,
      update(cache, { data }) {
        cache.modify({
          id: cache.identify(screeningOutcome.reference),
          fields: {
            screeningOutcome(_, { toReference }) {
              return toReference(data[key]);
            },
          },
        });
      },
      refetchQueries,
      awaitRefetchQueries: !optimistic,
    });
    refreshScreeningCounts(screeningOutcome.reference?.review?.id);
    refreshScreeningInsights(screeningOutcome.reference?.review?.id);
    return new ScreeningOutcome(response.data[key]);
  },
  async removeConflictResolution(
    context,
    { id, reference, cacheUpdate }
  ) {
    const response = await apolloClient.mutate({
      mutation: RemoveScreeningOutcomeConflictResolution,
      variables: {
        id,
      },
      update(cache, { data }) {
        cacheUpdate(
          cache,
          data.removeScreeningOutcomeConflictResolution
        );
      },
    });
    refreshScreeningCounts(reference?.review?.id);
    refreshScreeningInsights(reference?.review?.id);
    return new ScreeningOutcome(
      response.data.removeScreeningOutcomeConflictResolution
    );
  },
  async undoManualOverride(
    context,
    { id, reference, cacheUpdate, optimisticResponse }
  ) {
    let optimisticResponseToUse = undefined;
    if (optimisticResponse) {
      optimisticResponseToUse = {
        undoScreeningOutcomeManualOverride: {
          ...optimisticResponse,
          optimisticId: createUUID(),
        },
      };
    }
    const response = await apolloClient.mutate({
      mutation: UndoScreeningOutcomeManualOverride,
      variables: {
        id,
      },
      optimisticResponse: optimisticResponseToUse,
      update(cache, { data }) {
        const response = data.undoScreeningOutcomeManualOverride;
        cacheUpdate(cache, response);
      },
    });
    refreshScreeningCounts(reference?.review?.id);
    refreshScreeningInsights(reference?.review?.id);
    return new ScreeningOutcome(
      response.data.undoScreeningOutcomeManualOverride
    );
  },
};

export default {
  namespaced: true,
  actions,
};
